import type { Coupon, OutcomeGroup, SROW } from '../../../schema/coupon/entry.js';
import type { Definition } from '../../../schema/frameworks/entry.js';

export type ActionResult<TData, TErrorCode extends string = string> =
  | { status: 'ERROR'; error: string; reason?: TErrorCode }
  | { status: 'SUCCESS'; data: TData };

export type ActionError = Extract<ActionResult<unknown>, { status: 'ERROR' }>;

export type ActionStatus = ActionResult<unknown>['status'];

export type FormActionResult<TData> = ActionResult<TData> | { status: 'IDLE' } | { status: 'PENDING' };

export type FormActionStatus = FormActionResult<unknown>['status'];

export type ApiResult<TData, TErrorCode extends string = string> = ActionResult<TData, TErrorCode> & {
  httpStatus: HttpStatusCode;
};

export type WagerWorkerMessageMap = {
  ping: { payload: { msg: string }; response: string };
  // DrawForecast['drawResult'] should map to the same type for both forecast and result payload, two types not needed
  calculateBestBetRow: {
    payload: {
      coupon: Coupon & { key?: string };
      drawResult: ResultFilter;
      definition: Definition;
      // Optional cap. When provided, the worker returns at most `maxRows` SROWs
      // in total across all win divisions, preserving the grouping order.
      maxRows?: number;
    };
    response: SROW[][];
  };
  outcomeDistribution: {
    payload: { coupon: Coupon & { key?: string }; outcomeCount: number; definition: Definition };
    response: number[][];
  };
  totalNumberOfRows: {
    payload: { coupon: Coupon & { key?: string }; definition: Definition };
    response: number;
  };
};

// Boolean values should be used for masked/unmasked events. Boolean values will be replaced with real result.
// false: masked
// OutcomeGroup* should be used for the actual result to calculate best bet row on ie. multi forecast or actual result
export type ResultFilter = (false | OutcomeGroup)[];

type UpperSnakeChar =
  | 'A'
  | 'B'
  | 'C'
  | 'D'
  | 'E'
  | 'F'
  | 'G'
  | 'H'
  | 'I'
  | 'J'
  | 'K'
  | 'L'
  | 'M'
  | 'N'
  | 'O'
  | 'P'
  | 'Q'
  | 'R'
  | 'S'
  | 'T'
  | 'U'
  | 'V'
  | 'W'
  | 'X'
  | 'Y'
  | 'Z'
  | '_';

type UpperSnakeStartChar = Exclude<UpperSnakeChar, '_'>;

type UpperSnakeCaseRest<TString extends string> = TString extends `${infer C}${infer Rest}`
  ? C extends UpperSnakeChar
    ? UpperSnakeCaseRest<Rest>
    : never
  : true;

export type UpperSnakeCase<TString extends string> = TString extends `${infer C}${infer Rest}`
  ? C extends UpperSnakeStartChar
    ? UpperSnakeCaseRest<Rest> extends true
      ? TString
      : never
    : never
  : never;

export const HttpStatus = {
  Ok: 200,
  Created: 201,
  NoContent: 204,
  MovedPermanently: 301,
  FoundTemporaryRedirect: 302,
  NotModified: 304,
  BadRequest: 400,
  Unauthorized: 401,
  Forbidden: 403,
  NotFound: 404,
  TooManyRequests: 429,
  InternalServerError: 500,
  BadGateway: 502,
  ServiceUnavailable: 503,
  GatewayTimeout: 504,
} as const;

export type HttpStatusCode = (typeof HttpStatus)[keyof typeof HttpStatus];
